import { Plus } from "lucide-react";
import { useLocaleState, useTranslate } from "ra-core";
import { cn } from "@/lib/utils";

import type { CrmEvent } from "../types";
import { getMonthGrid, isSameDay, toDayKey } from "./calendarUtils";
import { useEventTypeLookup } from "./eventTypes";
import { formatEventTime } from "./formatEvent";

const groupByStartDay = (events: CrmEvent[]) => {
  const byDay = new Map<string, CrmEvent[]>();
  [...events]
    .sort((a, b) => a.start_at.localeCompare(b.start_at))
    .forEach((event) => {
      const key = toDayKey(new Date(event.start_at));
      byDay.set(key, [...(byDay.get(key) ?? []), event]);
    });
  return byDay;
};

/**
 * Month grid, Monday to Sunday. Events show on the day they start, colored by
 * type; clicking a day creates an event on it, clicking an event opens it.
 */
export const EventCalendar = ({
  month,
  events,
  onCreate,
  onSelect,
}: {
  month: Date;
  events: CrmEvent[];
  onCreate: (day: Date) => void;
  onSelect: (event: CrmEvent) => void;
}) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const lookupType = useEventTypeLookup();
  const weeks = getMonthGrid(month);
  const eventsByDay = groupByStartDay(events);
  const today = new Date();
  const weekday = new Intl.DateTimeFormat(locale, { weekday: "short" });
  const fullDate = new Intl.DateTimeFormat(locale, { dateStyle: "full" });

  return (
    <div className="rounded-lg border overflow-hidden bg-card">
      <div className="grid grid-cols-7 bg-muted/50 text-xs text-muted-foreground">
        {weeks[0].map((day) => (
          <div key={day.getDay()} className="px-2 py-1.5 capitalize">
            {weekday.format(day)}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {weeks.flat().map((day, index) => {
          const isCurrentMonth = day.getMonth() === month.getMonth();
          const isToday = isSameDay(day, today);
          const dayEvents = eventsByDay.get(toDayKey(day)) ?? [];
          return (
            <div
              key={toDayKey(day)}
              data-day={toDayKey(day)}
              onClick={() => onCreate(day)}
              className={cn(
                "group min-h-28 border-t p-1 flex flex-col gap-1 cursor-pointer hover:bg-muted/30",
                index % 7 !== 0 && "border-l",
                !isCurrentMonth && "bg-muted/20",
              )}
            >
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    "text-xs size-6 flex items-center justify-center rounded-full",
                    isToday && "bg-primary text-primary-foreground font-medium",
                    !isCurrentMonth && !isToday && "text-muted-foreground/60",
                  )}
                >
                  {day.getDate()}
                </span>
                <button
                  type="button"
                  aria-label={`${translate("resources.events.action.add")} – ${fullDate.format(day)}`}
                  className="opacity-0 group-hover:opacity-70 focus:opacity-70 rounded p-0.5 cursor-pointer"
                  onClick={(clickEvent) => {
                    clickEvent.stopPropagation();
                    onCreate(day);
                  }}
                >
                  <Plus className="size-3.5" />
                </button>
              </div>
              {dayEvents.map((event) => {
                const { color } = lookupType(event.type);
                const time = formatEventTime(new Date(event.start_at), locale);
                return (
                  <button
                    key={event.id}
                    type="button"
                    title={`${time} ${event.name}`}
                    onClick={(clickEvent) => {
                      clickEvent.stopPropagation();
                      onSelect(event);
                    }}
                    className="text-left text-xs rounded px-1.5 py-0.5 truncate border-l-2 hover:brightness-95 dark:hover:brightness-125 cursor-pointer"
                    style={{
                      borderLeftColor: color,
                      backgroundColor: `${color}1f`,
                    }}
                  >
                    <span className="font-medium tabular-nums">{time}</span>{" "}
                    {event.name}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};
